import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { AuthService } from '@app/core/services';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, map } from 'rxjs/operators';
import * as uuidV4 from 'uuid/v4';

export interface DictationLogEntry {
  sessionId: string;
  practiceId: string;
  providerId: string;
  patientId?: string;
  fieldContext: string;
  startTime: Date | any; // Can be Date or Firestore Timestamp
  endTime?: Date | any;
  duration?: number; // in seconds
  transcriptionLength?: number;
  success: boolean;
  errorMessage?: string;
  metadata?: {
    component: string;
    formField: string;
    userAgent?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class DictationLoggerService {
  constructor(private firestore: AngularFirestore, private authService: AuthService) {}

  /**
   * Start a new dictation session and log the initial event
   */
  startDictationSession(context: { patientId?: string; fieldContext: string; component: string; formField: string }): Observable<string> {
    const sessionId = uuidV4();

    return this.authService.auth$.pipe(
      switchMap(authState => {
        if (!authState || !authState.authenticated || !authState.currentTenantId || !authState.userId) {
          console.warn('⚠️ Cannot log dictation: No authenticated user or practice');
          return of(sessionId);
        }

        const logEntry: DictationLogEntry = {
          sessionId,
          practiceId: authState.currentTenantId,
          providerId: authState.userId,
          patientId: context.patientId,
          fieldContext: context.fieldContext,
          startTime: new Date(),
          success: false, // Will be updated when session ends
          metadata: {
            component: context.component,
            formField: context.formField,
            userAgent: navigator.userAgent,
          },
        };

        // Store in Firestore under practice collection
        return this.firestore
          .collection(`ClinicalPractice/${authState.currentTenantId}/DictationLogs`)
          .doc(sessionId)
          .set(logEntry)
          .then(() => {
            // console.log('📝 Dictation session started:', sessionId);
            return sessionId;
          })
          .catch(error => {
            console.error('❌ Failed to start dictation session:', error);
            return sessionId; // Return sessionId even if logging fails
          });
      }),
      catchError(error => {
        console.error('❌ Error in dictation session start:', error);
        return of(sessionId);
      })
    );
  }

  /**
   * End a dictation session and update the log entry
   */
  endDictationSession(
    sessionId: string,
    result: {
      success: boolean;
      transcriptionLength?: number;
      errorMessage?: string;
    }
  ): Observable<void> {
    return this.authService.auth$.pipe(
      switchMap(authState => {
        if (!authState || !authState.authenticated || !authState.currentTenantId) {
          console.warn('⚠️ Cannot update dictation log: No authenticated user or practice');
          return of(void 0);
        }

        const endTime = new Date();
        const updateData: Partial<DictationLogEntry> = {
          endTime,
          success: result.success,
        };

        // Only add non-undefined values
        if (result.transcriptionLength !== undefined) {
          updateData.transcriptionLength = result.transcriptionLength;
        }
        if (result.errorMessage !== undefined) {
          updateData.errorMessage = result.errorMessage;
        }

        return this.firestore
          .collection(`ClinicalPractice/${authState.currentTenantId}/DictationLogs`)
          .doc(sessionId)
          .get()
          .toPromise()
          .then(doc => {
            if (doc && doc.exists) {
              const data = doc.data() as DictationLogEntry;
              if (data.startTime) {
                // Handle both Firestore Timestamp and Date objects
                const startTimeMs = (data.startTime as any).toDate
                  ? (data.startTime as any).toDate().getTime()
                  : (data.startTime as Date).getTime();
                updateData.duration = Math.round((endTime.getTime() - startTimeMs) / 1000);
              }
            }

            return this.firestore
              .collection(`ClinicalPractice/${authState.currentTenantId}/DictationLogs`)
              .doc(sessionId)
              .update(updateData);
          })
          .then(() => {
            // console.log('✅ Dictation session ended:', sessionId, updateData);
          });
      }),
      catchError(error => {
        console.error('❌ Error ending dictation session:', error);
        return of(void 0);
      })
    );
  }

  /**
   * Get dictation logs for a practice (for analytics/reporting)
   */
  getDictationLogs(practiceId: string, limit: number = 100): Observable<DictationLogEntry[]> {
    return this.firestore
      .collection<DictationLogEntry>(`ClinicalPractice/${practiceId}/DictationLogs`, ref => ref.orderBy('startTime', 'desc').limit(limit))
      .valueChanges();
  }

  /**
   * Get dictation logs for a specific patient
   */
  getPatientDictationLogs(practiceId: string, patientId: string): Observable<DictationLogEntry[]> {
    return this.firestore
      .collection<DictationLogEntry>(`ClinicalPractice/${practiceId}/DictationLogs`, ref =>
        ref.where('patientId', '==', patientId).orderBy('startTime', 'desc')
      )
      .valueChanges();
  }
}
